/**
 * Lógica del asistente de campaña y del chequeo previo. Pura.
 *
 * Los pasos van POR NOMBRE, nunca por índice: es la misma lección que `CampanaNuevaRealPage.tsx`
 * (agregar un paso corría todos los índices y un `current === 4` terminaba mostrando otro paso).
 */

import type { AudienciaResumen, CampanaDetalle, Chequeo, EstadoMensajeOficial, EstadoPlantilla, GrupoAudiencia, MotivoExclusion, PlanTandas, ResultadoPrueba } from "../types";
import { ORDEN_GATES } from "../types";
import { MOTIVO_EXCLUSION, ESTADO_MENSAJE, detalleExclusion } from "./textos";
import { camposValidosGuardados, mapeoCompleto } from "./mapeo";
import { num, plural } from "../../campana-nueva/utils/format";
import { ZONA_MARCA, isoALocalEnZona, paisDeZona } from "./zonas";

/**
 * Los cuatro pasos (29/10/2026, R2 del rediseño): «A quién», «Qué les decís» (la plantilla y sus variables), «Cuándo»
 * (y cómo se dan de baja) y «Revisar y mandar» (la prueba y el confirmar). Los seis nombres de antes llegan todavía en
 * links viejos: `ALIAS_PASOS` (logic/asistente.ts) los lleva al paso nuevo.
 */
export type PasoAsistente = "audiencia" | "mensaje" | "cuando" | "revisar";
export const PASOS: PasoAsistente[] = ["audiencia", "mensaje", "cuando", "revisar"];

/** Un chequeo vale 10 minutos: después alguien pudo darse de baja, o cambiar el saldo o el cupo. */
export const VIGENCIA_CHEQUEO_MS = 10 * 60 * 1000;

/**
 * Filas de exclusión en el orden de ORDEN_GATES, sólo las > 0, con título y detalle de textos.ts. Las de las exclusiones
 * de la audiencia (28/10/2026) dicen cuáles con lo que trae el chequeo (`exclusiones`): con una sola lista que saca a
 * alguien, «Está en la lista «Empleados»»; con varias, o con las campañas, cuáles y a cuántos alcanza cada una.
 */
export function filasExclusion(c: Chequeo, fatigaDias: number, fatigaMaximo = 1): { motivo: MotivoExclusion; cantidad: number; titulo: string; detalle: string }[] {
  const ex = (c?.excluidos || {}) as Partial<Record<MotivoExclusion, number>>;
  return ORDEN_GATES
    .map((motivo) => ({ motivo, cantidad: Number(ex[motivo]) || 0 }))
    .filter((f) => f.cantidad > 0)
    .map((f) => ({ ...f, ...textoDeExclusion(f.motivo, c, fatigaDias, fatigaMaximo) }));
}

/** La fatiga dice la regla de la marca (`fatigaDias` y `fatigaMaximo`, de la config): los mensajes y los días, oficial + QR. */
function textoDeExclusion(motivo: MotivoExclusion, c: Chequeo, fatigaDias: number, fatigaMaximo: number): { titulo: string; detalle: string } {
  const base = { titulo: MOTIVO_EXCLUSION[motivo].titulo, detalle: detalleExclusion(motivo, fatigaDias, fatigaMaximo) };
  const exclusiones = c?.exclusiones;
  const cuales = (xs: { nombre: string; personas: number }[]) => xs.map((x) => `«${x.nombre}» (${num(x.personas)})`).join(", ");
  if (motivo === "en_lista_exclusion" && exclusiones) {
    const listas = exclusiones.listas.filter((l) => l.personas > 0);
    // las que valen para todas las campañas (29/10/2026) no se eligieron en ésta: se dice así
    const deSiempre = listas.some((l) => l.siempre);
    if (listas.length === 1) {
      return {
        titulo: `Está en la lista «${listas[0].nombre}»`,
        detalle: listas[0].siempre ? "Es una lista de exclusión que vale para todas las campañas de la marca." : "Es una de las listas de exclusión que elegiste en la audiencia.",
      };
    }
    if (listas.length > 1) return { ...base, detalle: deSiempre ? `Está en una de tus listas de exclusión: ${cuales(listas)}.` : `Está en una de las listas que elegiste: ${cuales(listas)}.` };
  }
  if (motivo === "recibio_otra_campana" && exclusiones) {
    const campanas = exclusiones.campanas.filter((x) => x.personas > 0);
    if (campanas.length) return { ...base, detalle: `Le llegó una de las campañas que elegiste: ${cuales(campanas)}.` };
  }
  return base;
}

const DIAS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
const dos = (n: number) => String(n).padStart(2, "0");

/**
 * Una fecha del plan en partes: el día ("AAAA-MM-DD") y la hora ("HH:MM"). Con `zona`, en la de la campaña (29/10/2026:
 * antes el asistente mostraba el plan en la hora del navegador, y una campaña en hora de México decía otras horas); sin
 * ella, en la del navegador, como siempre. "2026-09-24" es ese día, a las 00:00, en cualquier zona: `new Date("2026-09-24")`
 * es medianoche UTC, que en Argentina es el 23 a las 21 h, y el plan diría un día menos.
 */
export function partesDeFecha(s: string | Date | null | undefined, zona?: string): { dia: string; hora: string } | null {
  if (!s) return null;
  const soloDia = typeof s === "string" ? /^(\d{4}-\d{2}-\d{2})$/.exec(s) : null;
  if (soloDia) return { dia: soloDia[1], hora: "00:00" };
  const d = typeof s === "string" ? new Date(s) : s;
  if (isNaN(d.getTime())) return null;
  if (zona) {
    const l = isoALocalEnZona(d, zona);
    return { dia: l.slice(0, 10), hora: l.slice(11, 16) };
  }
  return { dia: `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`, hora: `${dos(d.getHours())}:${dos(d.getMinutes())}` };
}
type PartesDeFecha = NonNullable<ReturnType<typeof partesDeFecha>>;
const ddmm = (p: PartesDeFecha) => `${p.dia.slice(8, 10)}/${p.dia.slice(5, 7)}`;

/**
 * "Enviar a 612 personas" · "Enviar a 1 persona" · "Programar envío a 612 personas para el jue 24/09 10:00". Formato con
 * num(). La fecha y la hora van en la zona de la campaña, como la tarjeta «Vas a mandar» (antes, la del navegador: con
 * una campaña en hora de México, el botón decía otra hora que la tarjeta); fuera de la de la marca, dice de qué país.
 */
export function textoBotonConfirmar(envio: number, cuandoIso: string | null, zona: string = ZONA_MARCA): string {
  // «personas», como el resumen y la tarjeta «Vas a mandar» (29/10/2026: antes «contactos» acá y «personas» allá)
  const a = `${num(envio)} ${plural(envio, "persona", "personas")}`;
  const d = cuandoIso ? new Date(cuandoIso) : null;
  if (!d || isNaN(d.getTime())) return cuandoIso ? `Programar envío a ${a}` : `Enviar a ${a}`;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(isoALocalEnZona(d, zona));
  if (!m) return `Programar envío a ${a}`;
  const dia = DIAS[new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay()];
  const pais = zona !== ZONA_MARCA ? ` (hora de ${paisDeZona(zona)})` : "";
  return `Programar envío a ${a} para el ${dia} ${m[3]}/${m[2]} ${m[4]}:${m[5]}${pais}`;
}

const minutosDelDia = (p: PartesDeFecha) => Number(p.hora.slice(0, 2)) * 60 + Number(p.hora.slice(3, 5));

/**
 * El plan de tandas en una línea. Con la hora de cada tanda (el plan que simula el motor, con `hasta`):
 * "Con este público son 4 días: salen 250 por día entre las 19:00 y las 19:01. Termina el 22/10." · un día:
 * "Sale todo hoy, entre las 10:00 y las 10:02." · en el mismo minuto: "Sale todo hoy a las 10:00." · y lo que el
 * operador tiene que saber: lo que no llega antes de la fecha de fin y los mensajes de las campañas en cola que salen
 * antes (`plan.antes` cuenta mensajes, no campañas).
 * Sin la hora (un plan guardado por un backend anterior): "Hoy salen 250; el 24/09, 250; el 25/09, 112. Termina el
 * 25/09." · una tanda hoy: "Sale todo hoy." · con fecha programada: "El 24/09 salen …".
 * Con `zona` (la de la campaña), los días y las horas van en ella; sin ella, en la del navegador.
 */
export function textoPlan(plan: PlanTandas, hoy: Date, zona?: string): string {
  const tandas = (plan?.tandas || [])
    .map((t) => ({ fecha: partesDeFecha(t.fecha, zona), hasta: t.hasta ? partesDeFecha(t.hasta, zona) : null, cantidad: Number(t.cantidad) || 0 }))
    .filter((t) => t.cantidad > 0);
  const sinSalir = Number(plan?.sinSalir) || 0;
  if (!tandas.length) return sinSalir > 0 ? `Con este horario no sale ninguno de los ${num(sinSalir)}.` : "No hay nada para enviar.";
  const hoyDia = partesDeFecha(hoy, zona)?.dia;
  const cuando = (p: PartesDeFecha | null) => (p && p.dia === hoyDia ? "hoy" : p ? `el ${ddmm(p)}` : "sin fecha");
  if (tandas.every((t) => t.fecha && t.hasta)) {
    const desde = tandas.reduce((a, t) => (minutosDelDia(t.fecha as PartesDeFecha) < minutosDelDia(a) ? (t.fecha as PartesDeFecha) : a), tandas[0].fecha as PartesDeFecha);
    const hastaHora = tandas.reduce((a, t) => (minutosDelDia(t.hasta as PartesDeFecha) > minutosDelDia(a) ? (t.hasta as PartesDeFecha) : a), tandas[0].hasta as PartesDeFecha);
    // empieza y termina en el mismo minuto: «a las 10:00», no «entre las 10:00 y las 10:00»
    const mismoMinuto = desde.hora === hastaHora.hora;
    const franja = mismoMinuto ? `a las ${desde.hora}` : `entre las ${desde.hora} y las ${hastaHora.hora}`;
    const partes =
      tandas.length === 1
        ? [`Sale todo ${cuando(tandas[0].fecha)}${mismoMinuto ? " " : ", "}${franja}.`]
        : [
            `Con este público son ${num(tandas.length)} días: salen ${num(Number(plan.porDia) || Math.max(...tandas.map((t) => t.cantidad)))} por día ${franja}.`,
            `Termina ${cuando(partesDeFecha(plan.finEstimado, zona) ?? tandas[tandas.length - 1].hasta)}.`,
          ];
    if (sinSalir > 0) partes.push(`${num(sinSalir)} no ${plural(sinSalir, "llega", "llegan")} antes de la fecha de fin y no ${plural(sinSalir, "sale", "salen")}.`);
    const antes = Number(plan.antes) || 0;
    if (antes > 0) {
      partes.push(`Antes ${plural(antes, "sale", "salen")} ${num(antes)} ${plural(antes, "mensaje de otra campaña que ya está", "mensajes de otras campañas que ya están")} en cola.`);
    }
    return partes.join(" ");
  }
  if (tandas.length === 1) return `Sale todo ${cuando(tandas[0].fecha)}.`;
  const [primera, ...resto] = tandas;
  const cab = cuando(primera.fecha);
  const inicio = `${cab.charAt(0).toUpperCase()}${cab.slice(1)} salen ${num(primera.cantidad)}`;
  const siguientes = resto.map((t) => `${cuando(t.fecha)}, ${num(t.cantidad)}`);
  const fin = partesDeFecha(plan.finEstimado, zona) ?? tandas[tandas.length - 1].fecha;
  return `${[inicio, ...siguientes].join("; ")}. Termina ${cuando(fin)}.`;
}

/**
 * Punto de equilibrio: qué FRACCIÓN de los que reciben el mensaje tiene que volver a comprar para
 * cubrir el costo. (clienteEsperadoUSD / envio) / margenUSD. Devuelve una fracción (0.012 = 1,2 %):
 * quien la muestra la multiplica por 100. null si envio 0 o margen ≤ 0. Desde el 26/10/2026 los costos del WhatsApp oficial van sólo en dólares (decisión de Alan): sin tipo de cambio ni pesos.
 */
export function puntoEquilibrio(i: { clienteEsperadoUSD: number; envio: number; margenUSD: number }): number | null {
  const envio = Number(i.envio);
  const margen = Number(i.margenUSD);
  const costo = Number(i.clienteEsperadoUSD);
  if (!(envio > 0) || !(margen > 0) || !Number.isFinite(costo) || costo < 0) return null;
  return costo / envio / margen;
}

/**
 * Se puede confirmar: chequeo sin bloqueos, envio > 0, huella presente, con menos de 10 min desde que LLEGÓ (`recibidoEn`,
 * el reloj de la pantalla: el `calculadoAt` del servidor contra el reloj de la PC dejaba sin confirmar a una PC
 * adelantada), y no hay un chequeo en curso.
 */
export function puedeConfirmar(c: Chequeo | null, i: { calculando: boolean; ahora: Date; recibidoEn: number | null }): boolean {
  if (!c || i.calculando) return false;
  if ((c.bloqueos || []).length > 0) return false;
  if (!(Number(c.envio) > 0)) return false;
  if (!c.huella) return false;
  if (typeof i.recibidoEn !== "number" || !Number.isFinite(i.recibidoEn)) return false;
  return i.ahora.getTime() - i.recibidoEn < VIGENCIA_CHEQUEO_MS;
}

/**
 * Estados de plantilla con los que se puede seguir del mensaje (SPEC-BD B-10). Lista blanca: un estado
 * que no está (o que no vino) deja la campaña en «mensaje» (fail-closed).
 */
export const ESTADOS_PLANTILLA_PARA_SEGUIR: readonly EstadoPlantilla[] = ["received", "pending", "approved"];

/**
 * A cuántos de un grupo puede llegar WhatsApp como mucho: los que dijeron que sí Y tienen celular. El paso Audiencia
 * sumaba `optInTrue` y decía «Hasta 148 con consentimiento» cuando 38 de esos no tenían celular. Un backend anterior no
 * manda la intersección: el menor de los dos sigue siendo un tope verdadero (nunca promete de más).
 */
export function optInConCelularDe(g: Pick<GrupoAudiencia, "conCelular" | "optInTrue" | "optInConCelular">): number {
  if (typeof g?.optInConCelular === "number" && Number.isFinite(g.optInConCelular)) return Math.max(0, g.optInConCelular);
  return Math.max(0, Math.min(Number(g?.optInTrue) || 0, Number(g?.conCelular) || 0));
}

/** La audiencia guardada está completa según su tipo. Sin `tipo` es de Activa (los documentos viejos). */
export function audienciaCompleta(a: Pick<AudienciaResumen, "campanaActivaId" | "publicoId"> & { tipo?: AudienciaResumen["tipo"] } | null | undefined): boolean {
  if (!a) return false;
  // el Público de Activa: siempre (sin filtro = todos los que tienen WhatsApp)
  if (a.tipo === "publico") return true;
  return a.tipo === "base" ? !!a.publicoId : !!a.campanaActivaId;
}

/** Una plantilla archivada no sale: se elige otra en «Qué les decís». */
export const TEXTO_PLANTILLA_ARCHIVADA = "La plantilla se archivó: elegí otra";

/** Lo que todavía no deja llegar a «Revisar y mandar»: hasta qué paso se llega, por qué, y en qué paso se arregla. */
export type FrenoDelAsistente = { hasta: PasoAsistente; motivo: string; arreglo: PasoAsistente };

/**
 * Lo primero que le falta a lo que la campaña tiene guardado para llegar a «Revisar y mandar», o null si ya llega. Es la
 * regla de `maxPasoAlcanzable` (que es su `hasta`) con el porqué, para el panel «Tu campaña». En orden:
 * - sin la audiencia completa según su tipo → «audiencia» (el borrador nace en ese paso, desde el 29/10/2026);
 * - sin plantilla, o con una en `borrador`, `unsubmitted`, `rejected`, pausada, deshabilitada, eliminada, sin estado, o
 *   inutilizable con esta cuenta (`plantillaInutilizable`: simulada en modo real o de otra cuenta) → «mensaje». Así un F5
 *   o la alerta «Corregir» con una rechazada no saltan más adelante (SPEC-BD C1-C2);
 * - con la plantilla archivada (`plantillaArchivada`, del detalle: la lista viene sin las archivadas) → «mensaje», con su
 *   texto: antes se llegaba a «Revisar y mandar» y el chequeo decía «todavía no está aprobada»;
 * - con el mapeo incompleto → «mensaje»;
 * - con la plantilla en revisión (`received`, `pending`) → como mucho «cuando»: se completan las variables y cuándo sale
 *   mientras Meta la revisa, y «Revisar y mandar» (la prueba y el confirmar) espera la aprobación. Se arregla esperando:
 *   el estado en vivo está en «mensaje»;
 * - sin el horario o la baja elegidos → «cuando» (una campaña de antes del paso, sin los datos, sigue como siempre).
 *
 * `camposValidos`: los de `GET /campos` de la audiencia elegida. Sin ellos, los que la campaña guardada ya usa según su
 * tipo de audiencia (`camposValidosGuardados`): un «campo:puntos» de un público de la base no traba las variables
 * después de un F5.
 */
export function loQueFrena(
  c: Pick<CampanaDetalle, "plantilla" | "audiencia" | "mapeo"> & Partial<Pick<CampanaDetalle, "horarioElegido" | "baja">>,
  numerosPlantilla: number[],
  o: { plantillaInutilizable?: boolean; plantillaArchivada?: boolean; camposValidos?: readonly string[] } = {},
): FrenoDelAsistente | null {
  const en = (p: PasoAsistente, motivo: string, arreglo: PasoAsistente = p): FrenoDelAsistente => ({ hasta: p, motivo, arreglo });
  if (!audienciaCompleta(c?.audiencia)) return en("audiencia", "Falta elegir a quién le mandás");
  if (!c.plantilla) return en("mensaje", "Falta elegir la plantilla");
  if (o.plantillaArchivada) return en("mensaje", TEXTO_PLANTILLA_ARCHIVADA);
  const estado = c.plantilla.estado;
  if (o.plantillaInutilizable || !ESTADOS_PLANTILLA_PARA_SEGUIR.includes(estado)) {
    return en("mensaje", estado === "rejected" ? "Meta rechazó la plantilla" : "La plantilla elegida no se puede usar");
  }
  const tipo = c.audiencia?.tipo === "base" ? "base" : c.audiencia?.tipo === "publico" ? "publico" : "activa";
  const campos = o.camposValidos ?? camposValidosGuardados(tipo, c.mapeo);
  if (!mapeoCompleto(numerosPlantilla, c.mapeo || {}, campos)) return en("mensaje", "Faltan completar variables del mensaje");
  if (estado !== "approved") return en("cuando", "La plantilla espera la aprobación de Meta", "mensaje");
  if (c.horarioElegido === false || c.baja === null) return en("cuando", "Falta elegir cuándo sale y cómo se dan de baja");
  return null;
}

/**
 * Paso más lejano alcanzable según lo que la campaña ya tiene guardado (`loQueFrena`): sin nada que la frene,
 * «revisar». La prueba no es parte de la campaña: se manda (o se saltea con «Seguir sin probar») en «Revisar y mandar».
 */
export function maxPasoAlcanzable(
  c: Parameters<typeof loQueFrena>[0],
  numerosPlantilla: number[],
  o: Parameters<typeof loQueFrena>[2] = {},
): PasoAsistente {
  return loQueFrena(c, numerosPlantilla, o)?.hasta ?? "revisar";
}

/**
 * Título y tono de la tarjeta de prueba. Un 200 NO es un éxito por sí solo: Twilio rechaza con 200
 * y estado "fallido" (misma trampa que `campana-nueva/utils/resultadoPrueba.ts`). El fallo va
 * PRIMERO, incluso en simulado (el simulador también falla, por ejemplo con un número terminado en
 * 0000): una tarjeta roja nunca se tapa con «Registrada».
 *
 * La tarjeta la lee el comercio, que no sabe qué es Twilio: decía «Twilio no la mandó». Para él, el que
 * manda (o no) el mensaje es WhatsApp, como en «Se pausó: WhatsApp no responde».
 */
export function tituloPrueba(r: Pick<ResultadoPrueba, "estado" | "destino" | "codigoError" | "motivoError" | "simulado">):
  { titulo: string; tono: "ok" | "espera" | "error" | "simulado" } {
  const sim = r.simulado ? " (simulado)" : "";
  if (r.estado === "fallido" || r.estado === "no_entregado") {
    const motivo = (r.motivoError || "").trim() || "WhatsApp no dio el motivo";
    const codigo = r.codigoError ? ` (${r.codigoError})` : "";
    return { titulo: `WhatsApp no la mandó: ${motivo}${codigo}${sim}`, tono: "error" };
  }
  if (r.estado === "excluido" || r.estado === "cancelado" || r.estado === "control") {
    return { titulo: `No salió: ${ESTADO_MENSAJE[r.estado].label}${sim}`, tono: "error" };
  }
  if (r.estado === "sin_confirmacion") {
    return { titulo: `WhatsApp nunca confirmó si salió: tomala como no mandada${sim}`, tono: "error" };
  }
  if (r.simulado) return { titulo: "Registrada (simulado)", tono: "simulado" };
  if (r.estado === "entregado" || r.estado === "leido") {
    return { titulo: `Mandada a ${r.destino}`, tono: "ok" };
  }
  // Salió, pero falta el recibo de que llegó: sin él no se sabe si los avisos de WhatsApp entran (y con ellos las bajas).
  // El backend tampoco deja confirmar con esto (`pruebaVigente`, desde el 26/10).
  if (r.estado === "aceptado" || r.estado === "enviado") {
    return { titulo: `Salió a ${r.destino}: falta que WhatsApp confirme que le llegó`, tono: "espera" };
  }
  // desconocido, pendiente, reservado: no hay evidencia de que haya salido.
  return { titulo: "No sabemos si salió: lo estamos confirmando", tono: "espera" };
}

/** La prueba deja mandar la campaña: salió (o se registró, en simulado). Un fallo o un «no sabemos si salió», no. */
export function pruebaDejaSeguir(r: Parameters<typeof tituloPrueba>[0] | null | undefined): boolean {
  if (!r) return false;
  const { tono } = tituloPrueba(r);
  return tono === "ok" || tono === "simulado";
}

/** Estados en los que la prueba ya no cambia: se deja de consultar. */
export const ESTADOS_TERMINALES_PRUEBA = ["entregado", "leido", "fallido", "no_entregado", "sin_confirmacion"] as const;
export function pruebaTerminada(estado: string): boolean {
  return (ESTADOS_TERMINALES_PRUEBA as readonly string[]).includes(estado);
}

/**
 * La última prueba de la campaña, si sigue valiendo, sacada de la auditoría (el backend la anota en cada prueba). El
 * asistente la tenía sólo en memoria: un F5 en Confirmar volvía a Prueba y pedía mandar otra (USD 0,08 cada una) o
 * marcar «Seguir sin probar», por una que ya había salido y se había cobrado.
 *
 * La regla es la de la memoria: una prueba vale para la plantilla con la que se mandó, y cambiar de plantilla la
 * invalida (`guardarPlantilla` y `asociarPlantilla` la borran). Cambiar la audiencia o las variables no. Por eso se
 * mira de la más nueva para atrás: la primera prueba que aparece es la que vale (como en memoria, la nueva reemplaza a
 * la anterior aunque haya fallado) y una edición que cambió la plantilla, antes de llegar a ella, la invalida. Una
 * prueba anotada antes de este arreglo (sin `plantillaId`) o una edición vieja que tocó la plantilla sin decir si la
 * cambió no se aprovechan: ante la duda, se pide otra prueba.
 *
 * Devuelve lo que la tarjeta necesita (sin el texto: lleva datos de un contacto y no se guarda) y cuándo salió.
 */
export function pruebaDeLaAuditoria(c: Pick<CampanaDetalle, "auditoria" | "plantilla"> | null | undefined): { prueba: ResultadoPrueba; inicio: number } | null {
  const plantillaId = c?.plantilla?.id || "";
  if (!plantillaId) return null;
  const aud = Array.isArray(c?.auditoria) ? c.auditoria : [];
  const texto = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
  for (let i = aud.length - 1; i >= 0; i--) {
    const a = aud[i];
    const d = (a?.detalle && typeof a.detalle === "object" ? a.detalle : {}) as Record<string, unknown>;
    if (a?.accion === "editar") {
      const campos = Array.isArray(d.campos) ? d.campos : [];
      if (campos.includes("plantillaId") && d.cambioPlantilla !== false) return null;
      continue;
    }
    if (a?.accion !== "prueba") continue;
    const mensajeId = texto(d.mensajeId);
    const estado = texto(d.estado);
    if (d.plantillaId !== plantillaId || !mensajeId || !estado) return null;
    const inicio = new Date(a.at).getTime();
    return {
      prueba: {
        mensajeId,
        estado: estado as EstadoMensajeOficial,
        messageSid: null,
        costoReservadoUSD: Number(d.costoReservadoUSD) || 0,
        texto: "",
        destino: texto(d.destino) || "tu número",
        codigoError: texto(d.codigoError),
        motivoError: texto(d.motivoError),
        simulado: d.simulado === true,
      },
      inicio: Number.isFinite(inicio) ? inicio : 0,
    };
  }
  return null;
}
